/**
 * AppShell — dashboard chrome.
 *
 * Renders the top bar (logo · search · primary action · user · sign-out)
 * defined in `docs/newdesign.html`'s `.dash-top` mockup, with `children`
 * filling the remaining space (the `.dash-grid` of cards is rendered by
 * AppsList).
 *
 * The shell forwards its search-related props down to children via the
 * `searchProps` slot — AppsList mounts a search input that drives the
 * grid's filter state.
 */
import React, { ReactNode, useState } from 'react';

import { AuthUser } from '@/app/routing/guards/auth-route/AuthRoute';
import { clearSession } from '@/features/auth/session/authSession';
import CreateOrgModal from '@/features/org/components/settings/create-org-modal/CreateOrgModal';
import OrgSwitcher from '@/features/org/components/settings/org-switcher/OrgSwitcher';
import { useSyncActiveOrgFromUrl } from '@/features/org/hooks/use-active-org/useSyncActiveOrgFromUrl';
import { useOrgs } from '@/features/org/hooks/use-orgs/useOrgs';
import Logo from '@/shared/components/logo/Logo';
import { useHistory } from '@/shared/hooks/use-router/react-router-hooks';

import '@/features/dashboard/components/dashboard.styles.css';

type Props = {
  user: AuthUser;
  children: ReactNode;
  /** Optional slot rendered between the search input and the user chip,
   *  e.g. a "+ New app" primary button. */
  primaryAction?: ReactNode;
  /** Search input wiring. Pass undefined to hide the search field. */
  search?: {
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
  };
  /** When set, renders a left section-nav for an app's dashboard pages
   *  (currently just OTA Bundles). Omit on the apps grid. */
  appNav?: {
    appId: string;
    active?: 'ota';
  };
};

// OTA bundles — cube/package glyph (Phosphor "cube").
const NAV_ICON_OTA =
  'M222.72,67.9l-88-48.17a13.9,13.9,0,0,0-13.44,0l-88,48.18A14,14,0,0,0,26,80.18v95.64a14,14,0,0,0,7.28,12.27l88,48.18a13.92,13.92,0,0,0,13.44,0l88-48.18A14,14,0,0,0,230,175.82V80.18A14,14,0,0,0,222.72,67.9ZM127,30.25a2,2,0,0,1,1.92,0L212.51,76,128,122.24,43.49,76ZM39,177.57a2,2,0,0,1-1-1.75V86.66l84,46V223Zm177.92,0L134,223V132.64l84-46v89.16A2,2,0,0,1,217,177.57Z';

function NavIcon({ d }: { d: string }) {
  return (
    <svg width="15" height="15" viewBox="0 0 256 256" fill="currentColor" aria-hidden>
      <path d={d} />
    </svg>
  );
}

function AppSideNav({ appNav }: { appNav: NonNullable<Props['appNav']> }) {
  const { appId, active } = appNav;
  const base = `/apps/${encodeURIComponent(appId)}`;
  return (
    <nav className="dash-sidenav">
      <a className="dash-navitem dash-nav-back" href="/apps">
        ← All apps
      </a>
      <div className="dash-nav-section">Configure</div>
      <a className={`dash-navitem${active === 'ota' ? ' on' : ''}`} href={`${base}/ota`}>
        <NavIcon d={NAV_ICON_OTA} />
        OTA Bundles
      </a>
    </nav>
  );
}

export default function AppShell({ user, children, primaryAction, search, appNav }: Props) {
  const history = useHistory();
  const { activeSlug } = useOrgs();
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  // Land any /o/:slug/... URL into Redux + the activeOrg cookie so the
  // switcher and the X-Active-Org header stay honest with what the URL
  // says. Deep links, back/forward, hard-refresh — all covered.
  useSyncActiveOrgFromUrl();

  const onLogout = async () => {
    // Drop the cached session before leaving, so nothing can render against a
    // stale identity if the redirect is slow or interrupted.
    clearSession();
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
    } catch {
      /* fall through to redirect anyway */
    }
    window.location.assign('/login');
  };

  // Active-slug-aware deep links so the switcher's "Members" / "Org
  // settings" actions land on the right URL. Falls back to /dashboard
  // for the unauth/edge cases where the slug isn't hydrated yet.
  const orgRoot = activeSlug ? `/o/${activeSlug}` : '/dashboard';

  return (
    <div className="tile-dash-host">
      <header className="dash-top">
        <div
          className="logo"
          onClick={() => history.push(orgRoot + (activeSlug ? '/dashboard' : ''))}
        >
          <Logo width={80} />
        </div>

        {/* Org switcher — sits next to the logo so the user always knows
            which org's data they're looking at. */}
        <OrgSwitcher
          onOpenMembers={() => activeSlug && history.push(`/o/${activeSlug}/settings/members`)}
          onOpenSettings={() => activeSlug && history.push(`/o/${activeSlug}/settings/org`)}
          onCreateOrg={() => setShowCreateOrg(true)}
        />

        {search ? (
          <input
            type="text"
            className="dash-search"
            placeholder={search.placeholder ?? 'Search apps...'}
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
          />
        ) : null}

        <div className="dash-user">
          {primaryAction}
          <span className="vd" />
          <button
            type="button"
            className="chip chip-btn"
            title={`${user.email} — account settings`}
            onClick={() => history.push('/account')}
          >
            <span>{user.name}</span>
          </button>
          <button type="button" className="btn btn-g" onClick={onLogout}>
            Sign out
          </button>
        </div>
      </header>

      {appNav ? (
        <div className="dash-body">
          <AppSideNav appNav={appNav} />
          <div className="dash-main">{children}</div>
        </div>
      ) : (
        children
      )}

      {showCreateOrg && <CreateOrgModal onClose={() => setShowCreateOrg(false)} />}
    </div>
  );
}
